import type { ManualChapter } from './types'

export const forumChapter: ManualChapter = {
  id: 'forum',
  number: 4,
  title: 'Forum',
  description: 'Discussioni e community tematiche',
  icon: 'ChatBubbleLeftRightIcon',
  sections: [
    {
      id: 'structure',
      title: 'Struttura del Forum',
      requirement: '§2.3 Forum suddivisi per aree',
      content: `
## Struttura del Forum

Il forum è il cuore della community, dove i membri possono discutere, condividere esperienze e porre domande.

![Lista Forum](forum-list.png)
*La lista delle categorie del forum*

### Organizzazione

Il forum è organizzato in:

1. **Categorie** - Macro-aree tematiche (es. "Normativa", "Best Practice")
2. **Sottocategorie** - Argomenti specifici all'interno delle categorie
3. **Discussioni (Thread)** - Singole conversazioni
4. **Risposte (Post)** - Messaggi all'interno delle discussioni

### Tipi di Thread

| Tipo | Icona | Descrizione |
|------|-------|-------------|
| **Discussione** | 💬 | Conversazione generale su un argomento |
| **Domanda** | ❓ | Richiesta di aiuto con possibilità di "soluzione" |
| **Annuncio** | 📢 | Comunicazioni ufficiali (solo moderatori) |

### Gruppi

Esistono tre tipi di gruppi:

| Tipo | Accesso |
|------|---------|
| **Pubblico** | Visibile e accessibile a tutti |
| **Privato** | Visibile ma richiede approvazione |
| **Task Force** | Riservato ai membri del gruppo di lavoro |
`,
      screenshots: [
        {
          id: 'forum-list',
          path: '/assets/manual/forum-list.png',
          caption: 'Lista delle categorie del forum',
          alt: 'Pagina forum con elenco categorie'
        }
      ]
    },
    {
      id: 'navigation',
      title: 'Navigazione e Ricerca',
      requirement: '§2.3 Ricerca full-text nei contenuti e negli allegati',
      content: `
## Navigazione e Ricerca

### Navigare il Forum

1. Clicca su **"Forum"** nel menu laterale
2. Scegli una **categoria** di interesse
3. Sfoglia le **discussioni** presenti
4. Clicca su una discussione per leggerla

### Filtri Disponibili

Puoi filtrare le discussioni per:

| Filtro | Opzioni |
|--------|---------|
| **Stato** | Tutte, Aperte, Risolte |
| **Tipo** | Discussione, Domanda, Annuncio |
| **Ordinamento** | Più recenti, Più votate, Più commentate |

### Ricerca Full-Text

La piattaforma offre una potente ricerca che cerca:
- Nel **titolo** delle discussioni
- Nel **contenuto** dei messaggi
- Negli **allegati** (documenti PDF, Word, etc.)

**Come cercare:**
1. Usa la barra di ricerca in alto
2. Digita le parole chiave
3. Premi Invio o clicca sulla lente

> **Suggerimento**: Usa virgolette per cercare frasi esatte, es. "modello 231"
`
    },
    {
      id: 'reading',
      title: 'Leggere le Discussioni',
      content: `
## Leggere le Discussioni

![Categoria Forum](forum-category.png)
*Vista di una categoria con le discussioni*

### Visualizzazione Thread

Quando apri una discussione, vedrai:

- **Titolo** e informazioni autore
- **Messaggio originale** (primo post)
- **Risposte** ordinate cronologicamente
- **Allegati** scaricabili
- **Reazioni** (like, utile)

### Risposte Nidificate

Le risposte possono essere **gerarchiche**:
- Risposte dirette al thread principale
- Risposte a specifici messaggi (citazioni)

### Thread Ufficiali

I thread contrassegnati con badge **"Comitato Scientifico"** o **"Ufficiale"** contengono risposte validate dagli esperti.
`,
      screenshots: [
        {
          id: 'forum-category',
          path: '/assets/manual/forum-category.png',
          caption: 'Vista categoria con discussioni',
          alt: 'Elenco discussioni in una categoria'
        }
      ]
    },
    {
      id: 'create-thread',
      title: 'Creare una Nuova Discussione',
      requirement: '§2.3 Post con tag utenti, mention, allegati',
      content: `
## Creare una Nuova Discussione

### Procedura

1. Vai alla **categoria** appropriata
2. Clicca su **"Nuova Discussione"**
3. Compila il form:

| Campo | Descrizione | Obbligatorio |
|-------|-------------|--------------|
| **Tipo** | Discussione, Domanda, Annuncio | Sì |
| **Titolo** | Titolo chiaro e descrittivo | Sì |
| **Contenuto** | Il tuo messaggio | Sì |
| **Allegati** | File da condividere | No |

4. Clicca su **"Pubblica"**

### Editor di Testo

L'editor TipTap offre formattazione avanzata:

| Funzione | Shortcut |
|----------|----------|
| **Grassetto** | Ctrl+B |
| **Corsivo** | Ctrl+I |
| **Elenco puntato** | - + spazio |
| **Elenco numerato** | 1. + spazio |
| **Link** | Ctrl+K |
| **Codice** | \`\`\` |

### Allegare File

- Clicca sull'icona **📎** o trascina i file
- Formati supportati: PDF, Word, Excel, immagini
- **Limite**: 10 MB per file

### Menzioni (@)

Per menzionare un utente:
1. Digita **@** seguito dal nome
2. Seleziona l'utente dalla lista che appare
3. L'utente riceverà una notifica
`
    },
    {
      id: 'replying',
      title: 'Rispondere e Interagire',
      content: `
## Rispondere e Interagire

### Scrivere una Risposta

1. Scorri fino in fondo alla discussione
2. Usa il box **"Scrivi una risposta..."**
3. Formatta il testo come desideri
4. Clicca su **"Rispondi"**

### Citare un Messaggio

Per rispondere a un messaggio specifico:
1. Clicca su **"Cita"** sotto il messaggio
2. Il testo verrà inserito come citazione
3. Aggiungi la tua risposta

### Menzioni (@username)

Per attirare l'attenzione di qualcuno:
- Scrivi **@** e inizia a digitare il nome
- Seleziona dalla lista di suggerimenti
- L'utente riceverà una **notifica**

### Reazioni

| Reazione | Significato |
|----------|-------------|
| **👍 Mi piace** | Apprezzo questo messaggio |
| **✅ Utile** | Questo mi è stato utile |

### Marcare come Soluzione

Se la discussione è una **Domanda**:
- L'autore può marcare una risposta come **"Soluzione"**
- La soluzione appare evidenziata
- La discussione risulta "Risolta"
`
    },
    {
      id: 'moderation',
      title: 'Moderazione e Segnalazioni',
      requirement: '§2.3 Segnalazioni e moderazione avanzata',
      content: `
## Moderazione e Segnalazioni

### Segnalare Contenuti

Se trovi contenuti inappropriati:

1. Clicca sui **tre puntini** (⋮) del messaggio
2. Seleziona **"Segnala"**
3. Scegli il motivo:

| Motivo | Quando usarlo |
|--------|---------------|
| **Spam** | Pubblicità non richiesta |
| **Offensivo** | Contenuto offensivo o inappropriato |
| **Fuori tema** | Non pertinente alla discussione |
| **Duplicato** | Già discusso altrove |
| **Altro** | Specifica il motivo |

4. Aggiungi eventuali dettagli
5. Clicca su **"Invia segnalazione"**

### Cosa Succede Dopo

- Un moderatore valuterà la segnalazione
- Possibili azioni: rimozione, avviso all'autore, nessuna azione
- Riceverai notifica dell'esito (se appropriato)

### Thread Ufficiali

I contenuti marcati come **"Ufficiali"** sono:
- Redatti o validati dal Comitato Scientifico
- Riconoscibili dal badge distintivo
- Non modificabili dagli utenti normali
`
    }
  ]
}
